import { computed, reactive, ref } from 'vue';
import { required, helpers, minLength, email } from '@vuelidate/validators';
import {
	NUMBER_REGEX,
	NOTIFICATION_METHOD_TYPES,
	COMMENT_AND_FILE_VALIDATION_COMPLAINT_SUBJECT,
	MANDATORY_PAYMENT_VALIDATION_COMPLAINT_SUBJECT,
	NO_CARD_NUMBER_VALIDATION_TRANSACTION_TYPE
} from '../constants/constants';
import pattern from '../../../common/js/validators/pattern';
import { EMAIL_REGEXP, FULLNAME_REGEXP, ZIP_CODE_REGEXP } from '../../../common/js/constants/constants';

const MODEL = {
	posTerminalNumber: null,
	amexId: null,
	tradeCode: null,
	companyName: null,
	companyPostcode: null,
	settlement: null,
	address: null,
	contributorName: null,
	contactPhoneNumber: null,
	contactFax: null,
	contactEmail: null,
	complaintSubject: null,
	transactionType: null,
	transactionDate: null,
	cardNumber: null,
	licenceNumber: null,
	transactionAmount: null,
	transactionCurrency: null,
	disputeAmount: null,
	disputeCurrency: null,
	listAmount: null,
	payment: null,
	paymentCurrency: null,
	paymentDate: null,
	comment: null,
	file: null,
	notificationMethod: null,
	notificationMethodContact: null,
	captchaKey: null,
	captchaText: null
};
const files = ref([]);
const file = computed(() => (files.value.length ? files.value[files.value.length - 1] : null));
export const model = reactive({ ...MODEL });
const isEmailNotification = computed(() => model.notificationMethod === NOTIFICATION_METHOD_TYPES.EMAIL);
const isFaxNotification = computed(() => model.notificationMethod === NOTIFICATION_METHOD_TYPES.FAX);
const isLetterNotification = computed(() => model.notificationMethod === NOTIFICATION_METHOD_TYPES.LETTER);
export const useForm = () => {
	return { model, files, file, isEmailNotification, isFaxNotification, isLetterNotification };
};

const isAnyFilled = computed(() => model.posTerminalNumber || model.amexId || model.tradeCode);
const EMPTY_RULES = {};
Object.keys(MODEL).forEach((key) => (EMPTY_RULES[key] = {}));
const ALWAYS_ENFORCED_RULES = {
	posTerminalNumber: {
		required: () => isAnyFilled.value,
		minLength: helpers.withMessage('POS terminál száma mezőnek 8 karakter hosszúságúnak kell lennie.', minLength(8))
	},
	amexId: {
		required: () => isAnyFilled.value,
		minLength: helpers.withMessage('Amex üzletazonosító csak számokat tartalmazhat, és 10 karakter hosszúságúnak kell lennie.', minLength(10))
	},
	tradeCode: {
		required: () => isAnyFilled.value
	},
	companyName: {
		required: helpers.withMessage('Üzlet neve mező kitöltése kötelező.', required)
	},
	companyPostcode: {
		required: helpers.withMessage('Üzlet cím irányítószám mező kitöltése kötelező.', required),
		pattern: helpers.withMessage(
			'Üzlet cím irányítószám mező csak számokat tartalmazhat, és 4 karakter hosszúságúnak kell lennie.',
			pattern(ZIP_CODE_REGEXP)
		)
	},
	settlement: {
		required: helpers.withMessage('Üzlet cím település mező kitöltése kötelező.	', required)
	},
	address: {
		required: helpers.withMessage('Üzlet cím utca, házszám mező kitöltése kötelező.', required)
	},
	contributorName: {
		required: helpers.withMessage('Bejelentő neve mező kitöltése kötelező.', required),
		pattern: helpers.withMessage('Bejelentő neve mező értéke érvénytelen.', pattern(FULLNAME_REGEXP))
	},
	complaintSubject: {
		required: helpers.withMessage('Reklamáció tárgy kiválasztása kötelező.', required)
	},
	contactPhoneNumber: {
		required: helpers.withMessage('A kapcsolattartó telefonszámának kitöltése kötelező.', required),
		minLength: helpers.withMessage('A kapcsolattartó telefonszáma mező csak számokat tartalmazhat, és 9 karakter hosszúságúnak kell lennie.', minLength(9))
	},
	contactFax: {
		pattern: helpers.withMessage('Kapcsolattartó fax mező csak számokat tartalmazhat.', pattern(NUMBER_REGEX))
	},
	contactEmail: {
		email: helpers.withMessage('Kapcsolattartó e-mail mező kitöltése nem megfelelő.', email)
	},
	notificationMethod: {
		required: helpers.withMessage('Értesítés módjának kiválasztása kötelező.', required)
	}
};
const CARD_NUMBER_RULES = {
	cardNumber: {
		required: helpers.withMessage('Rendelkezésre álló kártyaszám mező kitöltése kötelező.', required),
		pattern: helpers.withMessage(
			'Rendelkezésre álló kártyaszám mező csak számokat tartalmazhat és minimum 4, maximum 19 karakter hosszúságú lehet.',
			minLength(4)
		)
	}
};
const TRANSACTION_AND_DISPUTE_RULES = {
	transactionType: {
		required: helpers.withMessage('Tranzakció típus kiválasztása kötelező.', required)
	},
	transactionDate: {
		required: helpers.withMessage('Tranzakció dátum mező kitöltése kötelező.', required)
	},
	licenceNumber: {
		required: helpers.withMessage('Engedélyszám mező kitöltése kötelező.', required)
	},
	transactionAmount: {
		required: helpers.withMessage('Tranzakció összege mező kitöltése kötelező.', required)
	},
	transactionCurrency: {
		required: helpers.withMessage('Tranzakció devizaneme kiválasztása kötelező.', required)
	},
	disputeAmount: {
		required: helpers.withMessage('Vitatott összeg mező kitöltése kötelező.', required)
	},
	disputeCurrency: {
		required: helpers.withMessage('Vitatott összeg devizanem kiválasztása kötelező.', required)
	}
};
const PAYMENT_RULES = {
	listAmount: {
		required: helpers.withMessage('Tételes listán szereplő utalandó összeg mező kitöltése kötelező.', required)
	},
	payment: {
		required: helpers.withMessage('Utalás összege mező kitöltése kötelező.', required)
	},
	paymentCurrency: {
		required: helpers.withMessage('Utalás összeg devizanem kiválasztása kötelező.', required)
	},
	paymentDate: {
		required: helpers.withMessage('Utalás dátuma mező kitöltése kötelező.', required)
	}
};
const COMMENT_AND_FILE_RULES = {
	comment: {
		required: helpers.withMessage('Megjegyzés mező kitöltése kötelező.', required)
	},
	file: {
		required: helpers.withMessage('Csatolt dokumentum feltöltése kötelező.', required)
	}
};
const notificationMethodContactRules = computed(() => ({
	emailRequired: isEmailNotification.value ? helpers.withMessage('Értesítési e-mail cím mező kitöltése közelező.', required) : {},
	emailPattern: isEmailNotification.value ? helpers.withMessage('Értesítési e-mail mező értéke érvénytelen.', pattern(EMAIL_REGEXP)) : {},
	faxRequired: isFaxNotification.value ? helpers.withMessage('Értesítési fax mező kitöltése közelező.', required) : {},
	faxPattern: isFaxNotification.value ? helpers.withMessage('Értesítési fax mező értéke érvénytelen.', pattern(NUMBER_REGEX)) : {},
	letterRequired: isLetterNotification.value ? helpers.withMessage('Értesítés levelezési cím mező kitöltése közelező.', required) : {}
}));
export const useValidations = () => {
	const enforceCommentAndFileRules = computed(() => model.complaintSubject === COMMENT_AND_FILE_VALIDATION_COMPLAINT_SUBJECT);
	const enforcePaymentRules = computed(() => model.complaintSubject === MANDATORY_PAYMENT_VALIDATION_COMPLAINT_SUBJECT);
	const enforceTransactionAndDisputeRules = computed(() => !enforceCommentAndFileRules.value && !enforcePaymentRules.value);
	const enforceCardNumberRules = computed(
		() => model.transactionType !== NO_CARD_NUMBER_VALIDATION_TRANSACTION_TYPE && !enforceCommentAndFileRules.value && !enforcePaymentRules.value
	);

	const rules = computed(() => ({
		...EMPTY_RULES,
		...ALWAYS_ENFORCED_RULES,
		...(enforcePaymentRules.value && PAYMENT_RULES),
		...(enforceCommentAndFileRules.value && COMMENT_AND_FILE_RULES),
		...(enforceTransactionAndDisputeRules.value && TRANSACTION_AND_DISPUTE_RULES),
		...(enforceCardNumberRules.value && CARD_NUMBER_RULES),
		notificationMethodContact: notificationMethodContactRules.value
	}));
	return { rules };
};
